/**
 * AI:
 * Status: 'confirmed'
 * Type: Component
 * SubType: Presentational
 * Reason: Detailed vertical stage-visualizer for the 7 pipeline phases of an EI
 *         import run. Per phase: state, timestamp (if any), phase-specific metrics.
 */
import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {EiImportDto} from '../../../../../../../../model/dto/entity/ei-import.dto';
import {EiImportRecordDto} from '../../../../../../../../model/dto/entity/ei-import-record.dto';
import {EiImportPhase} from '../../../../../../../../enum/ei-import-phase.enum';
import {EiImportExecutionStatus} from '../../../../../../../../enum/ei-import-execution-status.enum';

interface PhaseDescriptor {
    key: EiImportPhase;
    label: string;
    icon: string;
}

export interface PhaseMetric {
    label: string;
    value: string;
}

@Component({
    selector: 'ei-tooling-stage-vis-tab',
    templateUrl: './ei-tooling-stage-vis-tab.component.html',
    styleUrls: ['./ei-tooling-stage-vis-tab.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EiToolingStageVisTabComponent {

    @Input() parentImport: EiImportDto | null = null;
    @Input() record: EiImportRecordDto | null = null;

    readonly allPhases: PhaseDescriptor[] = [
        {key: EiImportPhase.INGESTION, label: 'Ingestion', icon: 'fa-regular fa-inbox'},
        {key: EiImportPhase.OBJECT_RESOLUTION, label: 'Objekt-Auflösung', icon: 'fa-regular fa-magnifying-glass'},
        {key: EiImportPhase.OBJECT_REVIEW, label: 'Objekt-Prüfung', icon: 'fa-regular fa-user-check'},
        {key: EiImportPhase.VALIDATION, label: 'Validierung', icon: 'fa-regular fa-circle-check'},
        {key: EiImportPhase.PERSISTENCE, label: 'Persistenz', icon: 'fa-regular fa-database'},
        {key: EiImportPhase.POST_PROCESSING, label: 'Nachbearbeitung', icon: 'fa-regular fa-gears'},
        {key: EiImportPhase.DONE, label: 'Abgeschlossen', icon: 'fa-regular fa-flag-checkered'},
    ];

    /**
     * Returns a status tag for a phase (done/current/pending with variants).
     */
    getPhaseStatus(phaseKey: EiImportPhase):
        'done' | 'current-running' | 'current-waiting' | 'current-error' | 'current-idle' | 'pending' {
        const current = this.parentImport?.phase;
        if (current == null) {
            return 'pending';
        }
        if (phaseKey < current) {
            return 'done';
        }
        if (phaseKey > current) {
            return 'pending';
        }
        const exec = this.parentImport?.executionStatus;
        if (exec === EiImportExecutionStatus.ERROR) {
            return 'current-error';
        }
        if (exec === EiImportExecutionStatus.RUNNING) {
            return 'current-running';
        }
        if (phaseKey === EiImportPhase.OBJECT_REVIEW) {
            return 'current-waiting';
        }
        if (phaseKey === EiImportPhase.DONE) {
            return 'done';
        }
        return 'current-idle';
    }

    /** Row container class. */
    phaseRowClass(_phase: PhaseDescriptor): string {
        return 'ei-phase-row';
    }

    /** Circle styling class (shared). */
    phaseCircleClass(_phase: PhaseDescriptor): string {
        return 'ei-phase-circle-lg border-circle flex align-items-center justify-content-center text-white';
    }

    /** Background color for the circle depending on status. */
    phaseCircleBackground(phase: PhaseDescriptor): string {
        const status = this.getPhaseStatus(phase.key);
        switch (status) {
            case 'done': return 'var(--green-500)';
            case 'current-running': return 'var(--orange-500)';
            case 'current-waiting': return 'var(--yellow-500)';
            case 'current-error': return 'var(--red-500)';
            case 'current-idle': return 'var(--blue-500)';
            case 'pending': return 'var(--surface-300)';
            default: return 'var(--surface-300)';
        }
    }

    /** Connector color for the line below the circle. */
    connectorColor(phase: PhaseDescriptor): string {
        const status = this.getPhaseStatus(phase.key);
        return status === 'done' ? 'var(--green-500)' : 'var(--surface-300)';
    }

    /** Icon variant for inside the circle based on state. */
    phaseStateIcon(phase: PhaseDescriptor): string {
        const status = this.getPhaseStatus(phase.key);
        switch (status) {
            case 'done': return 'fa-regular fa-check';
            case 'current-running': return 'fa-regular fa-spinner fa-spin';
            case 'current-waiting': return 'fa-regular fa-user-check';
            case 'current-error': return 'fa-regular fa-triangle-exclamation';
            case 'current-idle': return phase.icon;
            case 'pending': return phase.icon;
            default: return phase.icon;
        }
    }

    /** Short German label for the phase. */
    phaseLabel(phase: PhaseDescriptor): string {
        return phase.label;
    }

    /** Short German label for the phase state. */
    phaseStateLabel(phase: PhaseDescriptor): string {
        const status = this.getPhaseStatus(phase.key);
        switch (status) {
            case 'done': return 'Abgeschlossen';
            case 'current-running': return 'Läuft';
            case 'current-waiting': return 'Wartet auf Aktion';
            case 'current-error': return 'Fehler';
            case 'current-idle': return 'Aktuell';
            case 'pending': return 'Offen';
            default: return '';
        }
    }

    /** Returns a timestamp for a phase (only the current phase has a value). */
    phaseTimestamp(phase: PhaseDescriptor): Date | null {
        if (!this.parentImport?.phaseEnteredAt) {
            return null;
        }
        if (this.parentImport.phase === phase.key) {
            return this.parentImport.phaseEnteredAt;
        }
        return null;
    }

    /**
     * Returns a phase-specific list of metrics (best-effort, uses fields
     * already available on the import dto). Returns null if no metrics apply.
     */
    phaseMetrics(phase: PhaseDescriptor): PhaseMetric[] | null {
        const imp = this.parentImport;
        if (!imp) {
            return null;
        }
        switch (phase.key) {
            case EiImportPhase.INGESTION:
                return [
                    {label: 'Records angelegt', value: String(imp.recordCount ?? 0)}
                ];
            case EiImportPhase.OBJECT_RESOLUTION:
                return null;
            case EiImportPhase.OBJECT_REVIEW:
                return null;
            case EiImportPhase.VALIDATION:
                return null;
            case EiImportPhase.PERSISTENCE:
                return [
                    {label: 'Angelegt', value: String(imp.recordsCreated ?? 0)},
                    {label: 'Aktualisiert', value: String(imp.recordsUpdated ?? 0)},
                    {label: 'Übersprungen', value: String(imp.recordsSkipped ?? 0)},
                    {label: 'Fehler', value: String(imp.recordsErrors ?? 0)},
                ];
            case EiImportPhase.POST_PROCESSING:
                return null;
            case EiImportPhase.DONE:
                return null;
            default:
                return null;
        }
    }
}
